// Sons de première page — UN SEUL lecteur audio pour tout.
//
// POURQUOI UN SEUL LECTEUR : sur iPhone, démarrer un deuxième élément audio
// COUPE le premier (le gong se coupait net). La méthode éprouvée : un seul
// lecteur joue le gong JUSQU'AU BOUT de sa résonance, puis enchaîne
// automatiquement sur la boucle de vague. TANT QUE le gong n'est pas fini,
// la vague ne peut pas démarrer — c'est structurel, pas une minuterie.
//
// VOLUME DE LA VAGUE : iOS ignore element.volume (lecture seule sur Safari).
// Le lecteur passe donc par un GainNode Web Audio : gain 1,0 pendant le gong,
// puis le niveau du curseur « Puissance de la vague » (Admin → Réglages) quand
// la vague enchaîne. L'AudioContext est créé DANS le premier geste (exigence
// d'autoplay d'Apple). Le verrou anti double-déclenchement évite le bégaiement
// (iPhone envoie pointerdown PUIS touchend pour un même toucher).
//
// Règle absolue : la vague n'existe qu'au début, jamais en séance.
import { getAppSounds } from "@/lib/sounds";

let armed = false;
let player = null; // LE lecteur unique : gong puis vague
let srcOpening = "/audio/ouverture.wav";
let srcAmbiance = "/audio/vagues.wav";
let waveVolume = 0.15; // secours — la puissance vient des Réglages (wave_volume)
let phase = "idle"; // "idle" | "gong" | "wave"
let opened = false; // le gong ne sonne qu'une fois par ouverture de l'app
let silenced = false; // hors pages d'entrée : aucun redémarrage
let ringing = false; // anti double-déclenchement : un seul play() du gong

let audioCtx = null;
let masterGain = null; // 1,0 pendant le gong ; waveVolume pendant la vague
let routed = false;

function gainTarget() {
    return phase === "wave" ? waveVolume : 1.0;
}

// Applique le gain courant (appelé quand le curseur bouge ou la phase change)
function applyGain() {
    const target = gainTarget();
    if (masterGain && audioCtx) {
        masterGain.gain.setTargetAtTime(target, audioCtx.currentTime, 0.05);
    } else if (player) {
        try { player.volume = target; } catch { /* iOS : lecture seule */ }
    }
    window.__ravGain = phase === "wave" ? waveVolume : 0; // témoin : gain de la VAGUE
}

// Branche le lecteur sur le graphe Web Audio — depuis un geste utilisateur.
function ensureGraph() {
    if (!player || routed) return;
    try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return;
        if (!audioCtx) audioCtx = new AC();
        const src = audioCtx.createMediaElementSource(player);
        masterGain = audioCtx.createGain();
        masterGain.gain.value = gainTarget();
        src.connect(masterGain).connect(audioCtx.destination);
        routed = true;
        window.__ravWebAudio = true; // témoin : le volume passe par le GainNode
    } catch {
        // Web Audio indisponible : secours element.volume (ordinateur)
    }
}

// Le gong a FINI de résonner : le MÊME lecteur enchaîne sur la vague.
// Aucun autre chemin ne peut démarrer la vague.
function enterWave() {
    phase = "wave";
    if (silenced || !player) return;
    if (waveVolume <= 0) {
        window.__ravWaves = false;
        return; // curseur à zéro : silence total après le gong
    }
    player.loop = true;
    player.src = srcAmbiance;
    player.load();
    // Montée en fondu doux (~2,5 s) jusqu'au niveau du curseur
    if (masterGain && audioCtx) {
        const t = audioCtx.currentTime;
        masterGain.gain.cancelScheduledValues(t);
        masterGain.gain.setValueAtTime(0.0001, t);
        masterGain.gain.linearRampToValueAtTime(waveVolume, t + 2.5);
    } else {
        try { player.volume = waveVolume; } catch { /* iOS */ }
    }
    window.__ravGain = waveVolume;
    player
        .play()
        .then(() => {
            if (silenced) {
                player.pause();
                return;
            }
            window.__ravWaves = true;
        })
        .catch(() => {});
}

function ring() {
    ensureGraph(); // créé dans le geste — exigence iOS
    if (audioCtx && audioCtx.state === "suspended") audioCtx.resume().catch(() => {});
    if (silenced || !player) return; // pas encore prêt : le prochain toucher sonnera

    if (phase === "gong" && player.paused) {
        // Gong interrompu (page quittée en plein gong) : on le laisse FINIR sa
        // résonance — la vague n'entrera qu'à sa fin, comme toujours.
        player.play().catch(() => enterWave());
        return;
    }
    if (phase === "wave") {
        // Retour sur la page : la vague reprend si elle était en pause
        if (player.paused && waveVolume > 0) {
            applyGain();
            player.play().then(() => { window.__ravWaves = true; }).catch(() => {});
        }
        return;
    }
    if (opened || ringing) return; // un seul gong par ouverture de l'app

    // Premier gong : iPhone envoie pointerdown PUIS touchend — le verrou
    // `ringing` empêche le double play() qui faisait redémarrer le gong.
    ringing = true;
    phase = "gong";
    player.loop = false;
    if (!player.src.endsWith(srcOpening)) {
        player.src = srcOpening;
        player.load();
    }
    player.onended = () => enterWave(); // la vague n'arrive QU'APRÈS la fin du gong
    // Gain à fond pendant le gong (le curseur ne concerne QUE la vague)
    if (masterGain && audioCtx) {
        masterGain.gain.setValueAtTime(1.0, audioCtx.currentTime);
    } else {
        try { player.volume = 1.0; } catch { /* iOS */ }
    }
    window.__ravGain = 0;
    player
        .play()
        .then(() => {
            if (silenced) {
                player.pause();
                player.currentTime = 0;
                return;
            }
            opened = true;
            window.__ravDing = true;
        })
        .catch(() => {
            ringing = false;
            phase = "idle";
        });
}

export function armOpeningSound() {
    silenced = false; // pages d'entrée : le son est de nouveau le bienvenu
    // Navigation entre pages d'entrée (Gate → Bienvenue) : si le gong ou la
    // vague sont en cours, on annule IMMÉDIATEMENT tout fondu de sortie et on
    // reprend, sans attendre le réseau — le son traverse la navigation.
    if (player && phase !== "idle") {
        if (masterGain && audioCtx) {
            const t = audioCtx.currentTime;
            masterGain.gain.cancelScheduledValues(t);
            masterGain.gain.setValueAtTime(gainTarget(), t);
        }
        if (player.paused) {
            player
                .play()
                .then(() => {
                    if (phase === "wave") window.__ravWaves = true;
                })
                .catch(() => {});
        }
    }
    // Réglages relus FRAIS à CHAQUE passage sur une page d'entrée : un curseur
    // ou un bruitage changé dans l'admin s'applique dès la page suivante.
    getAppSounds().then((s) => {
        waveVolume = typeof s.waveVolume === "number" ? s.waveVolume : 0.15;
        srcOpening = s.opening;
        const prevAmbiance = srcAmbiance;
        srcAmbiance = s.ambiance;
        if (!player) {
            player = new Audio(srcOpening);
            player.preload = "auto";
            player.load();
            window.__ravAudio = player; // témoin de vérification
        }
        if (phase === "wave") {
            // Bruitage changé en admin pendant l'écoute : on le remplace
            if (srcAmbiance !== prevAmbiance && !player.paused) {
                player.src = srcAmbiance;
                player.load();
                player.play().catch(() => {});
            }
            applyGain();
            // Curseur descendu à 0 pendant l'écoute : on coupe la vague
            if (waveVolume <= 0 && !player.paused) {
                player.pause();
                player.currentTime = 0;
                window.__ravWaves = false;
            } else if (waveVolume > 0 && player.paused && !silenced) {
                player.play().then(() => { window.__ravWaves = true; }).catch(() => {});
            }
        }
    });
    if (armed) return;
    armed = true;
    // PLUS d'écoute globale des touchers : le gong ne part QUE sur le bouton
    // « Je découvre » (startOpeningSound), jamais en touchant l'écran au hasard
    // (fermer le popup d'installation ne déclenche plus rien).
}

// Déclenché par le bouton « Je découvre, c'est ma première visite » de la
// première page. Doit être appelé depuis le clic (geste utilisateur, exigence
// iOS). Le gong part immédiatement ; la vague enchaînera après sa résonance.
export function startOpeningSound() {
    silenced = false;
    if (player) {
        ring();
        return;
    }
    // Réglages pas encore arrivés : on retente dès que le lecteur est prêt
    getAppSounds().then((s) => {
        waveVolume = typeof s.waveVolume === "number" ? s.waveVolume : 0.15;
        srcOpening = s.opening;
        srcAmbiance = s.ambiance;
        if (!player) {
            player = new Audio(srcOpening);
            player.preload = "auto";
            player.load();
            window.__ravAudio = player;
        }
        ring();
    });
}

// Fondu de sortie via le gain — fonctionne aussi sur iPhone
function fadeOutAndPause(after) {
    if (!player || player.paused) {
        after?.();
        return;
    }
    if (masterGain && audioCtx) {
        const t = audioCtx.currentTime;
        masterGain.gain.cancelScheduledValues(t);
        masterGain.gain.setValueAtTime(masterGain.gain.value, t);
        masterGain.gain.linearRampToValueAtTime(0.0001, t + 1.1);
        setTimeout(() => {
            // Une page d'entrée a pu reprendre le son entre-temps (Gate →
            // Bienvenue) : dans ce cas on NE coupe PAS le gong en route
            if (!silenced) {
                masterGain.gain.setValueAtTime(gainTarget(), audioCtx.currentTime);
                return;
            }
            player.pause();
            masterGain.gain.setValueAtTime(gainTarget(), audioCtx.currentTime);
            after?.();
        }, 1150);
        return;
    }
    // Secours sans Web Audio : paliers locaux (element.volume est en lecture
    // seule sur iPhone — on compte sur une variable locale pour garantir l'arrêt)
    let v = 1.0;
    const step = () => {
        v -= 1 / 12;
        if (v > 0) {
            try { player.volume = Math.max(0, v); } catch { /* iOS */ }
            setTimeout(step, 90);
        } else {
            player.pause();
            try { player.volume = 1.0; } catch { /* iOS */ }
            after?.();
        }
    };
    step();
}

// Quand on quitte les pages d'entrée : le son s'éteint en fondu doux
export function fadeOpeningSound() {
    silenced = true;
    fadeOutAndPause(() => {
        window.__ravWaves = false;
    });
}

// Lancement d'une séance (ou arrivée sur l'accueil) : arrêt IMMÉDIAT, sans fondu
export function cutOpeningSound() {
    silenced = true;
    if (masterGain && audioCtx) {
        masterGain.gain.cancelScheduledValues(audioCtx.currentTime);
        masterGain.gain.setValueAtTime(gainTarget(), audioCtx.currentTime);
    }
    if (player && !player.paused) {
        player.pause();
    }
    window.__ravWaves = false;
}
